import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function Modal({ open, onClose, title, children, testid = "modal" }) {
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 0);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const activeFieldRef = useRef(null);

  // Scroll the currently focused field into the center of the visible viewport.
  // Critical for Safari iOS: when the on-screen keyboard slides up, iOS does NOT
  // auto-scroll the focused element inside an `overflow-y-auto` container that
  // lives inside a `position: fixed` modal. We have to do it manually.
  const scrollActiveIntoView = useCallback((delayMs = 280) => {
    const el = activeFieldRef.current;
    if (!el || !document.body.contains(el)) return;
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        // older Safari fallback
        el.scrollIntoView();
      }
    }, delayMs);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleResize = () => {
      const v = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      setVh(v);
      // Whenever the viewport shrinks (keyboard appears) or grows (keyboard hides),
      // re-center the focused field so it stays visible above the keyboard.
      if (activeFieldRef.current) scrollActiveIntoView(80);
    };
    handleResize();
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
      window.visualViewport.addEventListener("scroll", handleResize);
    }
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      document.body.style.overflow = prev;
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
        window.visualViewport.removeEventListener("scroll", handleResize);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [open, scrollActiveIntoView]);

  // Track the currently focused input/textarea/select inside the modal so we
  // can keep it visible as the keyboard slides up on iOS Safari.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    const isField = (t) => {
      if (!t) return false;
      const tag = t.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        t.isContentEditable === true
      );
    };

    const onFocusIn = (e) => {
      if (!isField(e.target)) return;
      activeFieldRef.current = e.target;
      scrollActiveIntoView();
    };
    const onFocusOut = (e) => {
      if (activeFieldRef.current === e.target) activeFieldRef.current = null;
    };

    panel.addEventListener("focusin", onFocusIn);
    panel.addEventListener("focusout", onFocusOut);
    return () => {
      panel.removeEventListener("focusin", onFocusIn);
      panel.removeEventListener("focusout", onFocusOut);
    };
  }, [open, scrollActiveIntoView]);

  if (!open || typeof document === "undefined") return null;

  // Render in a portal at document.body root so we escape any ancestor with
  // `transform`/`filter`/`will-change` that would otherwise break position:fixed.
  return createPortal(
    <div
      className="fixed left-0 right-0 top-0 z-50 flex items-stretch sm:items-center justify-center"
      data-testid={testid}
      style={{ height: `${vh}px` }}
    >
      <div
        className="absolute inset-0"
        style={{ background: "#0a0a0a", opacity: 1 }}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="glass-card rounded-none sm:rounded-md w-full sm:max-w-lg sm:mx-4 relative z-10 shadow-2xl flex flex-col"
        style={{
          height: "100%",
          maxHeight: `${vh}px`,
          paddingTop: "max(env(safe-area-inset-top), 0.25rem)",
        }}
      >
        <div className="flex items-center justify-between px-6 sm:px-8 pt-4 pb-3 border-b border-gold-subtle bg-obsidian shrink-0">
          <h3 className="font-display text-2xl text-gold-light">{title}</h3>
          <button
            onClick={onClose}
            className="text-muted hover:text-gold-light"
            data-testid={`${testid}-close`}
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          ref={scrollRef}
          className="overflow-y-auto modal-scroll-gutter px-6 sm:px-8 pt-6 pb-32 flex-1 min-h-0"
          style={{ WebkitOverflowScrolling: "touch" }}
          data-testid={`${testid}-scroll`}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
